/**
 * Bulk-import multiple iCal URLs (one property per URL).
 * Used for Avaibook (primary use case) but works for any iCal source.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Download, CheckCircle2, AlertCircle, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Modal from "@/components/Modal";

export default function BulkImportModal({ open, onClose, defaultSource = "avaibook", onImported }) {
    const [text, setText] = useState("");
    const [source, setSource] = useState(defaultSource);
    const [running, setRunning] = useState(false);
    const [results, setResults] = useState(null);
    const [error, setError] = useState("");

    const reset = () => {
        setText("");
        setResults(null);
        setError("");
        setRunning(false);
    };

    const close = () => {
        reset();
        onClose?.();
    };

    const submit = async (e) => {
        e?.preventDefault?.();
        const urls = text
            .split("\n")
            .map((l) => l.trim())
            .filter(Boolean);
        if (urls.length === 0) {
            setError("Pega al menos una URL iCal");
            return;
        }
        setRunning(true);
        setError("");
        setResults(null);
        try {
            const { data } = await api.post("/ical/bulk-import", {
                source,
                sources: urls.map((url) => ({ url })),
            });
            setResults(data);
            if (data.created > 0) {
                onImported?.();
            }
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setRunning(false);
        }
    };

    const totals = results
        ? {
              created: results.created,
              total: results.total,
              errored: results.results.filter((r) => r.status === "error").length,
              bookings: results.results.reduce((acc, r) => acc + (r.bookings_imported || 0), 0),
              tasks: results.results.reduce((acc, r) => acc + (r.tasks_created || 0), 0),
          }
        : null;

    return (
        <Modal
            open={open}
            onClose={close}
            title="Importar propiedades desde iCal (Avaibook, Airbnb, Booking…)"
            subtitle="Pega una URL iCal por línea. ZENTRA creará una propiedad por cada URL leyendo el nombre del feed (X-WR-CALNAME) y sincronizará las reservas inmediatamente."
            wide
            testId="bulk-import-modal"
            footer={
                results ? (
                    <button className="zentra-btn-primary" onClick={close} data-testid="bulk-import-done">
                        Hecho
                    </button>
                ) : (
                    <>
                        <button className="zentra-btn-ghost" onClick={close} disabled={running}>
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            form="bulk-form"
                            className="zentra-btn-accent"
                            disabled={running}
                            data-testid="bulk-import-submit"
                        >
                            <Download size={14} />
                            {running ? "Importando…" : "Importar y sincronizar"}
                        </button>
                    </>
                )
            }
        >
            {!results ? (
                <form id="bulk-form" onSubmit={submit} className="space-y-4">
                    <div>
                        <label className="zentra-label">Origen</label>
                        <select
                            className="zentra-select"
                            value={source}
                            onChange={(e) => setSource(e.target.value)}
                            disabled={running}
                            data-testid="bulk-import-source"
                        >
                            <option value="avaibook">Avaibook</option>
                            <option value="avantio">Avantio</option>
                            <option value="airbnb">Airbnb</option>
                            <option value="booking">Booking.com</option>
                            <option value="vrbo">Vrbo</option>
                            <option value="other">Otro</option>
                        </select>
                    </div>
                    <div>
                        <label className="zentra-label">URLs iCal (una por línea)</label>
                        <textarea
                            className="zentra-textarea font-mono-zentra text-xs min-h-[180px]"
                            placeholder={`https://www.avaibook.com/.../cal-property-1.ics\nhttps://www.avaibook.com/.../cal-property-2.ics\n...`}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            disabled={running}
                            data-testid="bulk-import-textarea"
                        />
                        <p className="text-[11px] text-zentra-muted mt-2">
                            {text.split("\n").filter((l) => l.trim()).length} URL(s) detectadas. Las propiedades se crearán
                            con nombre del feed y dirección vacía — podrás completarlas después.
                        </p>
                    </div>
                    {error && (
                        <div className="rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">
                            {error}
                        </div>
                    )}
                </form>
            ) : (
                <div className="space-y-3" data-testid="bulk-import-results">
                    {/* Summary banner */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <SummaryStat label="Creadas" value={totals.created} accent />
                        <SummaryStat label="Reservas" value={totals.bookings} />
                        <SummaryStat label="Tareas auto" value={totals.tasks} />
                        <SummaryStat label="Errores" value={totals.errored} danger={totals.errored > 0} />
                    </div>

                    {/* Per-URL results */}
                    <ul className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
                        {results.results.map((r, idx) => (
                            <li
                                key={idx}
                                className={`rounded-md border p-3 ${
                                    r.status === "ok"
                                        ? "border-emerald-200 bg-emerald-50/40"
                                        : "border-red-200 bg-red-50/60"
                                }`}
                                data-testid={`bulk-import-result-${idx}`}
                            >
                                <div className="flex items-start gap-3">
                                    {r.status === "ok" ? (
                                        <CheckCircle2 size={16} className="text-emerald-700 mt-0.5 flex-shrink-0" />
                                    ) : (
                                        <AlertCircle size={16} className="text-red-700 mt-0.5 flex-shrink-0" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        {r.status === "ok" ? (
                                            <>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-[10px] font-mono-zentra tracking-wider uppercase text-zentra-muted">
                                                        {r.code}
                                                    </span>
                                                    <Link
                                                        to={`/propiedades/${r.property_id}`}
                                                        className="font-semibold text-zentra-ink hover:text-zentra-primary"
                                                    >
                                                        {r.name}
                                                    </Link>
                                                </div>
                                                <div className="flex items-center gap-3 text-xs text-zentra-muted mt-1">
                                                    <span>{r.bookings_imported} reservas</span>
                                                    <span>·</span>
                                                    <span className="inline-flex items-center gap-1">
                                                        <Sparkles size={11} className="text-zentra-accent-strong" />
                                                        {r.tasks_created} limpiezas auto
                                                    </span>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <p className="text-sm font-medium text-red-900">Error importando</p>
                                                <p className="text-xs text-red-700 mt-0.5 break-all">{r.error}</p>
                                            </>
                                        )}
                                        <p className="text-[10px] text-zentra-muted font-mono-zentra truncate mt-1">
                                            {r.url}
                                        </p>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </Modal>
    );
}

function SummaryStat({ label, value, accent, danger }) {
    return (
        <div
            className={`rounded-md border p-3 ${
                danger
                    ? "border-red-200 bg-red-50"
                    : accent
                      ? "border-zentra-accent/30 bg-zentra-accent/10"
                      : "border-zentra-border bg-zentra-bg/50"
            }`}
        >
            <p className="text-[10px] font-semibold tracking-widest uppercase text-zentra-muted">{label}</p>
            <p
                className={`font-display font-bold text-2xl mt-1 ${
                    danger ? "text-red-700" : accent ? "text-zentra-accent-strong" : "text-zentra-ink"
                }`}
            >
                {value}
            </p>
        </div>
    );
}
